// Case Resolution System Types

import { MessageImage } from './message';

export enum CaseStatus {
  UNREAD = 'UNREAD',
  READ = 'READ',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED'
}

export enum AdminActionType {
  COMPLETE_TRADE = 'COMPLETE_TRADE',
  CANCEL_TRADE = 'CANCEL_TRADE',
  REFUND_BOTH = 'REFUND_BOTH',
  NO_ACTION = 'NO_ACTION',
  SET_DUE_DATE = 'SET_DUE_DATE',
  MESSAGE_SENT = 'MESSAGE_SENT',
  CASE_REOPENED = 'CASE_REOPENED'
}

export interface Case {
  id: string;
  tradeId: string;
  conversationId: string;
  status: CaseStatus;
  createdAt: string;
  updatedAt: string;
  dueDate: string | null;
  summonedBy: number;
  alsoRequestedBy: number | null;
  senderUsername: string;
  receiverUsername: string;
  tradeNumber: string;
  trade?: {
    createdAt: string;
    sender?: {
      id: number;
      username: string;
      avatarUrl?: string;
    };
    receiver?: {
      id: number;
      username: string;
      avatarUrl?: string;
    };
  };
}

export interface CaseListResponse {
  cases: Case[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCases: number;
    pageSize: number;
  };
}

export interface CaseMessage {
  id: string;
  content: string;
  senderId: number;
  senderUsername: string;
  receiverId: number;
  createdAt: string;
  readAt: string | null;
  sender: {
    id: number;
    username: string;
    avatarUrl?: string;
    isAdminMessage: boolean;
  };
  images?: MessageImage[];
}

export interface AdminAction {
  id: string;
  caseId: string;
  adminId: number | null;
  adminUsername: string | null;
  actionType: AdminActionType;
  details: Record<string, any> | null;
  createdAt: string;
}

// Party (sender/receiver) shape on the admin case-detail trade.
// Avatar enrichment fields (country/isElite/border*) are supplied by the
// backend case-detail response so AvatarWithFlag can render flags + Elite
// treatment consistently with Trade Alerts. `country` is mapped from the
// user's shippingCountry on the backend.
export interface TradePartyDetails {
  id: number;
  username: string;
  email?: string;
  avatarUrl?: string;
  country?: string | null;
  isElite?: boolean;
  showAnimatedBorder?: boolean;
  borderColor1?: string | null;
  borderColor2?: string | null;
}

export interface TradeDetails {
  id: string;
  status: string;
  tradeNumber: string;
  createdAt: string;
  totalValue: number;
  // Single timestamp set when the sender marks the trade shipped (no per-side field).
  shippedAt?: string | null;
  sender: TradePartyDetails;
  receiver: TradePartyDetails;
  items: Array<{
    id?: string;
    name: string;
    setCode?: string;
    setName?: string;
    set?: string;
    quantity: number;
    condition: string;
    pointValue: number;
    foil?: boolean;
    language?: string;
    edition?: string;
    scryfallID?: string;
    collectorNumber?: string;
    rarity?: string;
  }>;
}

export interface CaseDetailResponse {
  case: Case & {
    trade: TradeDetails;
  };
  messages: CaseMessage[];
  adminActions: AdminAction[];
  dividerTimestamp: string;
  conversationId: string;
}

export interface TradeCaseStatusResponse {
  exists: boolean;
  case?: {
    id: string;
    status: CaseStatus;
    createdAt: string;
    dueDate: string | null;
  };
  summonedBy?: string;
  alsoRequestedBy?: string | null;
  summonedAt?: string;
}

export interface SummonAdminResponse {
  caseId: string;
  status: CaseStatus;
  message: string;
  conversationId: string;
}

export interface CaseBadgeCountResponse {
  unreadCount: number;
}

export interface AdminActionRequest {
  actionType: AdminActionType;
  details?: {
    dueDate?: string;
    notes?: string;
  };
}

export interface AdminActionResponse {
  success: boolean;
  case: {
    id: string;
    status: CaseStatus;
    updatedAt: string;
  };
}

export interface SendAdminMessageRequest {
  content: string;
}

export interface SendAdminMessageResponse {
  success: boolean;
}

export interface SetDueDateRequest {
  dueDate: string;
}

export interface SetDueDateResponse {
  success: boolean;
  case: {
    id: string;
    dueDate: string;
    status: CaseStatus;
  };
}
